import { useState, useEffect } from "react";
import { Share, X, Plus } from "lucide-react";

/**
 * 2026-05-16 — iOS PWA install nudge.
 *
 * Why this exists: Apple hides the "Add to Home Screen" affordance
 * behind the Share sheet which most non-technical customers never
 * find. This banner appears once per device for iOS Safari users
 * who haven't installed yet, with clear visual instructions.
 *
 * Hidden automatically when:
 *   - not iOS Safari (Chrome iOS, Android, desktop all skip it)
 *   - the app is already running standalone (already installed)
 *   - the user dismissed it (localStorage flag, 30-day cooldown)
 *
 * Drop into any page that should encourage install:
 *   <IosInstallBanner appName="EP Staff" accent="blue" />
 */
export function IosInstallBanner({
  appName = "Europacific",
  accent = "blue",
  storageKey = "ios-install-banner-dismissed",
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      // 1. Already running as installed PWA? bail.
      const standalone =
        window.navigator.standalone === true ||
        window.matchMedia("(display-mode: standalone)").matches;
      if (standalone) return;

      // 2. iOS Safari only — bots/Chrome iOS/Android handled by their own install prompts
      const ua = window.navigator.userAgent || "";
      const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      // Chrome iOS UA contains "CriOS"; Firefox iOS contains "FxiOS"; we want Safari proper
      const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
      if (!isIos || !isSafari) return;

      // 3. Respect cooldown — re-show after 30 days
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const ts = parseInt(stored, 10);
        if (!isNaN(ts) && Date.now() - ts < 30 * 24 * 60 * 60 * 1000) return;
      }

      // Tiny delay so it doesn't fight the page's load animation
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    } catch {
      /* fail-safe: never break the host page */
    }
  }, [storageKey]);

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, String(Date.now()));
    } catch {}
    setShow(false);
  };

  if (!show) return null;

  const palette = {
    blue: { bg: "from-blue-600 to-indigo-700", icon: "bg-white/15" },
    amber: { bg: "from-amber-500 to-orange-600", icon: "bg-white/15" },
    emerald: { bg: "from-emerald-600 to-teal-700", icon: "bg-white/15" },
  };
  const p = palette[accent] || palette.blue;

  return (
    <div
      className={`fixed bottom-3 left-3 right-3 z-[9999] rounded-2xl shadow-2xl text-white bg-gradient-to-r ${p.bg} animate-slide-up`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="ios-install-banner"
      role="dialog"
      aria-label="Install app instructions"
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${p.icon} flex items-center justify-center text-xl`}>
          📲
        </div>
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <p className="font-semibold mb-1">Install {appName} on your home screen</p>
          <p className="text-white/90 text-xs flex items-center flex-wrap gap-x-1.5 gap-y-1">
            Tap
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 font-medium">
              <Share className="w-3 h-3" />
              Share
            </span>
            then
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 font-medium">
              <Plus className="w-3 h-3" />
              Add to Home Screen
            </span>
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss install banner"
          data-testid="ios-install-banner-dismiss"
          className="flex-shrink-0 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <style>{`
        @keyframes slide-up {
          from { transform: translateY(120%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
      `}</style>
    </div>
  );
}

export default IosInstallBanner;
